import "./style.scss";
import Input from "shared/ui/Input";
import React, { FC, useContext, useState } from "react";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import Button from "shared/ui/Button";
import SelectCountry from "shared/ui/SelectCountry";
import { createTrustfull } from "features/trustfull";
import {
    createTrustfullEmail,
    createTrustfullPhone,
    createTrustfullEmailPhone
} from "features/trustfull";
import { UserContext } from "features/account";
import Alert from "shared/ui/Alert";
import { decryptApiBodyToJson } from "shared/api/cryptApiBody";
import { canStartDetection, consumeDetection, DetectionTool, NO_VERIFICATION_LEFT } from "features/credits";
import { Button_Style, Button_Type } from "shared/ui/Button/Button";
import { AnimatePresence, motion } from "framer-motion";
import { stepVariants } from "utils/utils";
import { ReactComponent as IconNext } from "assets/img/icons/icon_arrow-Right.svg";

import AddFilterDocument from "components/AddFilterDocument";
import HeaderSection from "components/HeaderSection";
import WatermarkTextSet from "components/Watermark/WatermarkTextSet";
import VerificationModeSelector from "components/VerificationModeSelector";
import { useNavigate } from "react-router-dom";

type CheckAnotherOneProps = {
    isScanning: boolean;
    setIsScanning: React.Dispatch<React.SetStateAction<boolean>>;
    setAnotherOneChecked: React.Dispatch<React.SetStateAction<boolean>>;
    setCheckAnotherOne: React.Dispatch<React.SetStateAction<boolean>>;
    setIsScanned: React.Dispatch<React.SetStateAction<boolean>>;
    setTrustfullData: React.Dispatch<React.SetStateAction<any>>;
    backToChoice: () => void;
};

const CheckAnotherOne: FC<CheckAnotherOneProps> = ({
    isScanning,
    setIsScanning,
    setAnotherOneChecked,
    setCheckAnotherOne,
    setIsScanned,
    setTrustfullData,
    backToChoice,
}) => {
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [verificationMode, setVerificationMode] = useState<
        'complete' | 'email' | 'phone' | 'email_phone'
    >('complete');

    const { user } = useContext(UserContext);
    const type = user?.type ?? ''; // Assurez-vous que 'type' est toujours une chaîne de caractères

    const navigate = useNavigate();

    const handleVerificationModeChange = (
        mode: 'complete' | 'email' | 'phone' | 'email_phone'
    ) => {
        setVerificationMode(mode);
        setErrorMessage(""); // Réinitialiser les erreurs
    };

    // Appel API pour Trustfull selon le mode sélectionné
    const checkSomeone = async (values: any) => {
        // Garde d'entrée : 3 vérifications gratuites par mois pour cet outil, puis solde.
        if (!(await canStartDetection(type, DetectionTool.identity))) {
            setErrorMessage(NO_VERIFICATION_LEFT);
            return;
        }
        try {
            setIsScanning(true);
            setErrorMessage(""); // Réinitialiser les erreurs

            let response;

            // Assurer que les valeurs sont des chaînes vides si elles sont undefined
            const emailValue = values.email || "";
            const telValue = values.tel || "";
            const documentCountryValue = values.documentCountry || "";

            switch (verificationMode) {
                case 'email':
                    // Vérification email uniquement avec le nouvel endpoint
                    // console.log('Vérification email seul:', emailValue);
                    response = await createTrustfullEmail(emailValue, type);
                    break;

                case 'phone':
                    // Vérification téléphone uniquement avec le nouvel endpoint
                    const phoneNumber = documentCountryValue + telValue;
                    // console.log('Vérification téléphone seul:', phoneNumber);
                    response = await createTrustfullPhone(phoneNumber, type);
                    break;

                case 'email_phone':
                    // Vérification email + téléphone avec le nouvel endpoint
                    const phoneNumberCombined = documentCountryValue + telValue;
                    response = await createTrustfullEmailPhone(
                        emailValue,
                        phoneNumberCombined,
                        type,
                    );
                    break;

                case 'complete':
                default:
                    // Vérification complète (tous les champs) - utilise l'ancien endpoint
                    // console.log('Vérification complète:', values);
                    // ⚠️ createTrustfull est positionnelle :
                    // (phone_number, email, first_name, last_name, place_of_birth, date_of_birth, type)
                    // Ce formulaire ne collecte pas les champs de naissance : les passer
                    // explicitement en "" pour que `type` reste bien le 7e argument.
                    // Sans `type` à la racine du body, le middleware userLoggedIn du backend
                    // rejette la requête en 401 ("Vous avez été déconnecté...").
                    response = await createTrustfull(
                        documentCountryValue + telValue,
                        emailValue,
                        values.prenom || "",
                        values.nom || "",
                        "",
                        "",
                        type,
                    );
                    break;
            }

            // console.log('Réponse API:', response);

            // Vérifier si la réponse et la propriété data existent avant de déchiffrer
            if (response && response.data && response.status === 200) {
                const responseDecrypted = decryptApiBodyToJson(response.data); // Déchiffrer les données reçues
                // console.log('Données déchiffrées:', responseDecrypted);

                setErrorMessage(""); // Effacer le message d'erreur si la requête réussit
                await consumeDetection(type, DetectionTool.identity); //3 gratuites/mois, puis 1 vérification débitée
                setAnotherOneChecked(true);
                setCheckAnotherOne(false);
                setIsScanned(true);
                setTrustfullData(responseDecrypted);
            } else {
                console.error('Erreur API - Status:', response?.status);
                setErrorMessage(
                    "Une erreur s'est produite lors de la vérification des informations.",
                );
            }
        } catch (error: any) {
            console.error("Erreur lors de la vérification:", error);

            // Gestion d'erreurs plus détaillée
            if (error.response) {
                console.error(
                    'Erreur de réponse:',
                    error.response.status,
                    error.response.data,
                );

                // Messages d'erreur spécifiques selon le type d'erreur
                if (error.response.status === 400) {
                    setErrorMessage(
                        "Données invalides. Veuillez vérifier les informations saisies.",
                    );
                } else if (error.response.status === 401) {
                    setErrorMessage(
                        "Erreur d'authentification. Veuillez vous reconnecter.",
                    );
                } else if (error.response.status === 404) {
                    setErrorMessage(
                        "Service de vérification temporairement indisponible.",
                    );
                } else if (error.response.status === 500) {
                    setErrorMessage(
                        "Erreur serveur. Veuillez réessayer plus tard.",
                    );
                } else {
                    setErrorMessage(
                        `Erreur ${error.response.status}: ${error.response.data?.message || 'Une erreur s\'est produite'}`,
                    );
                }
            } else if (error.request) {
                console.error('Erreur de réseau:', error.request);
                setErrorMessage(
                    "Erreur de connexion. Veuillez vérifier votre connexion internet.",
                );
            } else {
                console.error('Erreur:', error.message);
                setErrorMessage("Une erreur inattendue s'est produite.");
            }
        } finally {
            setIsScanning(false);
        }
    };

    // Fonction pour créer le schéma de validation dynamique
    const getValidationSchema = () => {
        let schema: any = {};

        if (verificationMode === 'complete') {
            schema.nom = Yup.string()
                .min(2, "Le nom doit contenir au moins 2 caractères.")
                .max(20, "Le nom ne peut pas dépasser 20 caractères.")
                .matches(
                    /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-\\\\']+$/,
                    "Seules les lettres, espaces, tirets, apostrophes et accents sont autorisés.",
                )
                .required("Le nom est requis");
            schema.prenom = Yup.string()
                .min(2, "Le prénom doit contenir au moins 2 caractères.")
                .max(50, "Le prénom ne peut pas dépasser 50 caractères.")
                .matches(
                    /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-\\\\']+$/,
                    "Seules les lettres, espaces, tirets, apostrophes et accents sont autorisés.",
                )
                .required("Le prénom est requis");
        }

        if (
            verificationMode === 'email' ||
            verificationMode === 'email_phone' ||
            verificationMode === 'complete'
        ) {
            schema.email = Yup.string()
                .email("L'adresse e-mail est invalide")
                .required("L'adresse e-mail est requise");
        }

        if (
            verificationMode === 'phone' ||
            verificationMode === 'email_phone' ||
            verificationMode === 'complete'
        ) {
            schema.tel = Yup.string()
                .matches(/^[0-9]+$/, "Le numéro de téléphone doit contenir uniquement des chiffres.")
                .min(8, "Le numéro de téléphone doit contenir au moins 8 chiffres.")
                .max(15, "Le numéro de téléphone ne peut pas dépasser 15 chiffres.")
                .required("Le numéro de téléphone est requis");
            schema.documentCountry = Yup.string().required(
                "L'indicatif du pays est requis",
            );
        }

        return Yup.object(schema);
    };

    // Fonction pour obtenir le texte d'aide selon le mode sélectionné
    const getHelpText = () => {
        switch (verificationMode) {
            case 'email':
                return "Seule l'adresse email sera vérifiée.";
            case 'phone':
                return "Seul le numéro de téléphone sera vérifié.";
            case 'email_phone':
                return "L'adresse email et le numéro de téléphone seront vérifiés.";
            case 'complete':
            default:
                return "Tous les champs seront vérifiés (nom, prénom, email, téléphone).";
        }
    };

    const [steps, setSteps] = useState([
        { isCurrent: true, isDone: false },
        { isCurrent: false, isDone: false },
        { isCurrent: false, isDone: false },
    ]);
    const handlePreviousStep = () => {
        const currentStep = steps.findIndex((step) => step.isCurrent);
        if (currentStep > 0) {
            handleUpdateStep(currentStep - 1);
        }
    };

    const handleUpdateStep = (step: number) => {
        setSteps(
          steps.map((s, index) => {
              if (index === step) {
                  return { isCurrent: true, isDone: false };
              } else if (index < step) {
                  return { isCurrent: false, isDone: true };
              }
              return { isCurrent: false, isDone: false };
          })
        );
    };

    const handleNextStep = () => {
        const currentStep = steps.findIndex((step) => step.isCurrent);
        if (currentStep < steps.length - 1) {
            handleUpdateStep(currentStep + 1);
        }
    };

    return (
        <div className="CheckAnotherOne card h-100">
            <Formik
                initialValues={{
                    nom: "",
                    prenom: "",
                    email: "",
                    tel: "",
                    documentCountry: "",
                }}
                onSubmit={(values) => {
                    // console.log('Soumission du formulaire avec les valeurs:', values);
                    // console.log('Mode de vérification:', verificationMode);
                    checkSomeone(values);
                }}
                validationSchema={getValidationSchema()}
                enableReinitialize={true}
            >
                {(formik) => (
                  <Form className="d-flex flex-column h-100">
                      <AnimatePresence mode="wait">
                          {steps[0].isCurrent && (
                            <motion.div
                              key="step1"
                              variants={stepVariants}
                              initial="initial"
                              animate="animate"
                              exit="exit"
                              transition={stepVariants.transition}
                            >
                                {/* Cases à cocher pour le mode de vérification */}
                                <div>
                                    <VerificationModeSelector
                                      multiple={false}              // ou false si tu veux un seul choix
                                      value={verificationMode}                // string[] si multiple = true
                                      onChange={(next) => setVerificationMode(next)}
                                    />

                                </div>

                            </motion.div>
                          )}
                          {steps[1].isCurrent && (
                            <motion.div
                              key="step2"
                              variants={stepVariants}
                              initial="initial"
                              animate="animate"
                              exit="exit"
                              transition={stepVariants.transition}
                            >
                                <div>
                                    <h3>Informations du tiers</h3>
                                    <p className="mb-1">Veuillez saisir les informations ci dessous.</p>


                                    {errorMessage && <Alert type="error">{errorMessage}</Alert>}

                                    {/* Champs conditionnels selon le mode sélectionné */}
                                    {verificationMode === 'complete' && (
                                      <>
                                          <Input
                                            required
                                            placeholder="Nom"
                                            name="nom"
                                            value={formik.values.nom}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            errorMessage={
                                                formik.touched.nom && formik.errors.nom
                                                  ? formik.errors.nom
                                                  : ""
                                            }
                                          />
                                          <Input
                                            required
                                            placeholder="Prénom"
                                            name="prenom"
                                            value={formik.values.prenom}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            errorMessage={
                                                formik.touched.prenom && formik.errors.prenom
                                                  ? formik.errors.prenom
                                                  : ""
                                            }
                                          />
                                      </>
                                    )}

                                    {(
                                      verificationMode === 'email' ||
                                      verificationMode === 'email_phone' ||
                                      verificationMode === 'complete'
                                    ) && (
                                      <Input
                                        required
                                        placeholder="Email"
                                        name="email"
                                        type="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        errorMessage={
                                            formik.touched.email && formik.errors.email
                                              ? formik.errors.email
                                              : ""
                                        }
                                      />
                                    )}

                                    {(
                                      verificationMode === 'phone' ||
                                      verificationMode === 'email_phone' ||
                                      verificationMode === 'complete'
                                    ) && (
                                      <div className="d-flex gap-1">
                                          <div style={{ width: "40%" }}>
                                              {/* Select pour les indicatifs des pays */}
                                              <Field
                                                name="documentCountry"
                                                value={formik.values.documentCountry}
                                                id="documentCountry"
                                              >
                                                  {({ field, country }: any) => (
                                                    <SelectCountry
                                                      field={field}
                                                      country={country}
                                                      phone={true}
                                                    />
                                                  )}
                                              </Field>
                                              {formik.touched.documentCountry &&
                                                formik.errors.documentCountry && (
                                                  <div className="text-danger small mt-1">
                                                      {formik.errors.documentCountry}
                                                  </div>
                                                )}
                                          </div>
                                          <div style={{ width: "70%" }}>
                                              <Input
                                                required
                                                placeholder="Téléphone"
                                                name="tel"
                                                type="tel"
                                                value={formik.values.tel}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, "");
                                                    formik.setFieldValue("tel", value);
                                                }}
                                                onBlur={formik.handleBlur}
                                                errorMessage={
                                                    formik.touched.tel && formik.errors.tel
                                                      ? formik.errors.tel
                                                      : ""
                                                }
                                              />
                                          </div>
                                      </div>
                                    )}
                                </div>

                            </motion.div>
                          )}
                          <div
                            className={`mt-1 d-flex justify-content-${
                              !steps[0].isCurrent ? "center" : "center"
                            } w-100`}
                          >
                              {steps[0].isCurrent && (
                                <div
                                  className="d-flex w-100 justify-content-between mt-2"
                                >
                                    <Button
                                      buttonType="button"
                                      label="Retour"
                                      style={Button_Style.OUTLINED}
                                      onClick={() => navigate(0)}
                                      disabled={isScanning}
                                    />
                                    <Button
                                      onClick={handleNextStep}
                                      label={"Étape suivante"}
                                      disabled={!verificationMode}
                                      icon={<IconNext fill={"#ffffff"} />}
                                    />
                                </div>
                              )}
                              {!steps[0].isCurrent && (
                                <div
                                  className="d-flex w-100 justify-content-between"
                                  style={{
                                      marginTop: "auto",
                                  }}
                                >
                                    <Button
                                      buttonType="button"
                                      label="Retour"
                                      style={Button_Style.OUTLINED}
                                      onClick={handlePreviousStep}
                                      disabled={isScanning}
                                    />
                                    <Button
                                      buttonType="submit"
                                      label={isScanning ? "Vérification..." : "Vérifier"}
                                      disabled={isScanning}
                                    />
                                </div>
                              )}
                          </div>
                      </AnimatePresence>


                  </Form>
                )}
            </Formik>
        </div>
    );
};

export default CheckAnotherOne;
